import { Button, Icon, Input, Table, TextArea } from '@scuf/common';
import { LabelDescription } from 'components';
import useUser from 'helpers/hooks/useUser';
import useFetch from 'helpers/hooks/useFetch';
import { CreatNewRmaModelMf, rmaItemsMf } from 'models/rmamanufatura/detailsRma';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { notificationAtom, COMPANY_MANAGER, companyAtom } from 'resources';
import { createRma, uploadFile, createRmaManufacturing } from 'resources/api/';
import { CreatNewRma, CreatNewRmaMf } from 'resources/atoms/rmaAtom';
import { orderCartFileAtom } from 'resources/atoms/spareParts';
import { getCompanyByCnpj, getCompanyById } from 'resources/api/usermanager_api/addCustomer';
import { CompanyDTO } from 'models/userManager/addCustomer';
import { RMAMANUFATURA, REPAIR_CENTER } from 'resources';
import { DataTable } from '@scuf/datatable';
import { Modal } from '@scuf/common';

type OccupationImage = { occupation: string; image: File | Blob | null; imageCode: string };
const initializeOccupationImages = (count: number): OccupationImage[] => {
    return Array.from({ length: count }, () => ({ occupation: '', image: null, imageCode: '' }));
};

function NewRmaSummary() {
    const navigate = useNavigate();
    const { t } = useTranslation(['rmamanufatura', 'common', 'user']);
    const [loading, setLoading] = useState<boolean>(false);
    const [details, setDetails] = useRecoilState<CreatNewRmaModelMf>(CreatNewRmaMf);
    const [company] = useRecoilState<CompanyDTO>(companyAtom);
    const setNotification = useSetRecoilState(notificationAtom);
    const [file, setFile] = useRecoilState<File | null>(orderCartFileAtom);
    const [uploadXLSXSuccess, setUploadXLSXSuccess] = useState<boolean>(true);
    const [occupationImages, setOccupationImages] = useState<OccupationImage[]>(initializeOccupationImages(4));
    const [rmaCartIds, setRmaCartIds] = useState<number[]>([]);
    const { user } = useUser();
    const { user: userDetails } = useUser();
    const [detailsTable, setDetailsTable] = useState<any>([]);
    const [observacao, setObservacao] = useState<string>("");
    const [nameResponsible, setNameResponsible] = useState<string>("");
    const [phoneResponsible, setPhoneResponsible] = useState<string>("");
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [createdRmaId, setCreatedRmaId] = useState<number | null>(null);


    const [, getItemsInCartManufacturing, loadingTable] = useFetch<{ ssoId: string }>({
        url: RMAMANUFATURA.getItemsInCartManufacturing,
        method: 'GET'
    });

    const [, getManufacturingImages, loadingImages] = useFetch<{ rmaCartId: number }, { data: string[] }>({
        url: RMAMANUFATURA.getManufacturingImages,
        method: 'GET',
        onError: 'Erro Get Imagens',
    });

    useEffect(() => {
        if (user?.ssoid) {
            findItemInCart();
        }
        getCompany();
    }, [user]);

    const findItemInCart = async () => {
        if (user) {

            const volumeTypeMap: Record<number, string> = {
                1: 'NF (integral)',
                2: 'Caixas',
                3: 'Paletes',
                4: 'Unidades'
            };

            await getItemsInCartManufacturing({ ssoId: user.ssoid }, current => {
                const transformed = current.data.map((item: any) => ({
                    Id: item.id,
                    TipoVolume: volumeTypeMap[item.rmaCartManufacturing?.volumeType] ?? 'Não especificado',
                    NfHoneywell: item.rmaCartManufacturing?.nfHoneywell ?? '',
                    Model: item.model ?? '',
                    GBE: item.rmaCartManufacturing?.gbe,
                    ProblemLocation: item.rmaItemsEquipment?.problemLocation?.name ?? '',
                    Description: item.rmaItemsEquipment?.problemTypeDescription ?? '-',
                    SerialNumber: item.rmaItemsEquipment?.serialNumber ?? '',
                    Quantity: item.rmaCartManufacturing?.quantity ?? '-',
                    Cell: ''
                }));

                setDetailsTable(transformed);

                const rmaCartIdsFromCart = current.data
                    .map((item: any) => item.id)
                    .filter((id: number | undefined): id is number => typeof id == 'number');
                setRmaCartIds(rmaCartIdsFromCart);
                if (rmaCartIdsFromCart.length > 0) {
                    handleGetImages(rmaCartIdsFromCart);
                }
            });
        }
    };

    const handleGetImages = async (rmaCartIds: number[]): Promise<void> => {
        if (rmaCartIds.length === 0) return;
        const rmaCartId = rmaCartIds[0];

        await getManufacturingImages({ rmaCartId }, (resp) => {
            const imagesData = (resp.data || []) as {
                imageData: string;
                contentType?: string;
                fileName: string;
            }[];
            const updatedOccupationImages = initializeOccupationImages(4);
            imagesData.slice(0, 4).forEach((imageData, index) => {
                const base64String = imageData.imageData;
                const contentType = imageData.contentType || 'image/jpeg';
                updatedOccupationImages[index] = {
                    occupation: imageData.fileName,
                    image: null,
                    imageCode: `data:${contentType};base64,${base64String}`,
                };
            });
            setOccupationImages(updatedOccupationImages);
        });
    };

    const postXLSX = async (id: number) => {
        setLoading(true);
        if (file != undefined) {
            let resp: { status: number; data: any; message?: any } = await uploadFile(file, id, userDetails?.ssoid!, 'NFE');
            setUploadXLSXSuccess(resp.status == 200);
            if (resp.status != 200 && resp.message == undefined)
                if (resp.status == 200) {
                    setLoading(false);
                    navigate('/rma-manufatura');
                    setFile(null);
                }
            navigate('/rma-manufatura');
            setLoading(false);
        }
    };

    const getCompany = async () => {
        const company = Number(user?.company)
        const cnpj = await getCompanyById(company)
        return cnpj.data.cnpj
    };

    const sendCreatNewRma = async () => {
        setDetails({ ...details, ssoId: userDetails?.ssoid! });
        const { rmaItems, ...newRma } = details;
        const cnpj = await getCompany();

        const payload = {
            ...newRma,
            rmaItems,
            statusId: 1,
            cnpjCompany: cnpj,
            ssoId: userDetails?.ssoid!,
            observacao: observacao,
            companyId: userDetails?.company,
            eventoSsoId: ["-"],
            nameResponsible: nameResponsible,
            phoneResponsible: phoneResponsible
        };

        console.log("Payload: ", payload);
        try {
            setLoading(true);
            const resp = await createRmaManufacturing(payload);
            setDetails(resp.data);
            setCreatedRmaId(resp.data);
            setShowSuccessModal(true);
            postXLSX(resp.data);
            setFile(null);
            setLoading(false);
            setNotification((old: any) => ({ ...old, message: t('rmamanufatura:translation.RmaCreated'), type: 'success' }));
        } catch (error: any) {
            setNotification(old => ({ ...old, message: t('rmamanufatura:translation.ErrorCreatingRma') }));
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/rma-manufatura');
        setFile(null);
    };

    return (
        <div>
            <div>
                <div className="container">
                    <h1>
                        <Icon root="common" name="user-edit" size="large" /> {t('rmamanufatura:translation.InformationResponsible')}
                    </h1>
                    <div className="form2Columns">
                        <Input
                            indicator="required"
                            placeholder={t('user:info.Name')}
                            label={t('user:info.Name')}
                            value={nameResponsible}
                            onChange={(value) => setNameResponsible(value)}
                        />
                        <Input
                            indicator="required"
                            placeholder={t('rmamanufatura:translation.PhoneOrCell')}
                            label={t('rmamanufatura:translation.PhoneOrCell')}
                            value={phoneResponsible}
                            onChange={(value) => setPhoneResponsible(value)}
                        />
                    </div>
                    <h1>
                        <Icon root="global" name="shopping-bag" size="large" /> {t('common:translation.Equipments')} ({loadingTable ? '...' : detailsTable.length})
                    </h1>
                    <div className="div-datatable2" style={{ marginBottom: '40px', marginTop: '40px' }}>
                        <div style={{ marginRight: '15px' }}>
                            <DataTable
                                data={detailsTable}
                                rows={500}
                                scrollable={true}
                                virtualScroll={true}
                                scrollHeight="800px"
                            >
                                <DataTable.Column field="TipoVolume" header={t('rmamanufatura:translation.VolumeType')} />
                                <DataTable.Column field="NfHoneywell" header={t('rmamanufatura:translation.NFHoneywell')} />
                                <DataTable.Column field="Model" header={t('rmamanufatura:translation.Model')} sortable={true} />
                                <DataTable.Column field="GBE" header={'GBE'} sortable={true} />
                                <DataTable.Column field="Quantity" header={t('rmamanufatura:translation.Quantity')} />
                                <DataTable.Column
                                    field="ProblemLocation"
                                    header={t('common:translation.Problem')}
                                ></DataTable.Column>
                                <DataTable.Column
                                    field="Description"
                                    header={t('common:translation.Description')}
                                    sortable={true}
                                />
                                <DataTable.Column
                                    field="SerialNumber"
                                    header={t('rmamanufatura:translation.SerialNumber')}
                                    sortable={true}
                                />
                            </DataTable>
                        </div>
                    </div>
                    <div>
                        <h1>
                            <Icon root="common" name="device-add" size="large" />{' '}
                            {t('rmamanufatura:translation.Observation')}
                        </h1>
                        <TextArea
                            fluid={true}
                            className="textArea"
                            maxLength={500}
                            placeholder={t('rmamanufatura:translation.EnterYourObservations')}
                            value={observacao}
                            onChange={(value) => setObservacao(value)} />
                    </div>
                    <h1 style={{ marginBottom: '20px' }}>
                        <Icon root="common" name="user-edit" size="large" /> {t('rmamanufatura:translation.ImagesRMA')}
                    </h1>
                    <div className="image-previews">
                        {loadingImages ? (
                            Array.from({ length: 4 }).map((_, index) => (
                                <div key={index} className="image-preview" style={{ position: 'relative', marginBottom: '10px', marginTop: '35px' }}>
                                    <div style={{ height: '150px', width: '200px', border: '2px dashed #ccc', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f7f7f7' }}>
                                        <Icon name="refresh" size="large" loading={true} />
                                    </div>
                                </div>
                            ))
                        ) : (
                            occupationImages.map((item, index) => (
                                item.imageCode && (
                                    <div key={index} className="image-preview" style={{ position: 'relative', marginBottom: '10px', marginTop: '35px' }}>
                                        <img src={item.imageCode} alt={`preview-${index}`} />
                                        <div className="image-actions" style={{ display: 'flex', justifyContent: 'center', position: 'absolute', top: 0, right: 0 }}>
                                        </div>
                                    </div>
                                )
                            ))
                        )}
                    </div>

                    <div className="buttonsSearch">
                        <Button
                            content={t('common:actions.Cancel')}
                            size="small"
                            textTransform={true}
                            type="secondary"
                            className="buttonCancelSecondary"
                            onClick={() => handleCancel()}
                            loading={loading}
                            disabled={loading}
                        />
                        <Button
                            content={t('common:actions.Create')}
                            size="small"
                            textTransform={true}
                            loading={loading}
                            onClick={() => sendCreatNewRma()}
                            disabled={loading || loadingTable || !nameResponsible || !phoneResponsible}
                        />
                    </div>
                </div>
            </div>
            <Modal
                open={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                size="small"
                closeIcon={false}
                dimmer="blurring"
            >
                <Modal.Header>{t('rmamanufatura:translation.RmaCreated')}</Modal.Header>
                <Modal.Content>
                    <p><strong>{t('rmamanufatura:translation.RmaNumber')}</strong>{createdRmaId}</p>
                    <p><strong>{t('rmamanufatura:translation.NameResponsible')}</strong>{nameResponsible}</p>
                    <p><strong>{t('rmamanufatura:translation.Contact')}</strong>{phoneResponsible}</p>
                    <Button
                        content="OK"
                        onClick={() => {
                            setShowSuccessModal(false);
                            navigate('/rma-manufatura');
                        }}
                    />
                </Modal.Content>
            </Modal>

        </div>
    );
}

export default NewRmaSummary;